// Общее содержание КП для всех юрлиц: ставка НДС, условия поставки и строка
// «КП № … от … · юрлицо · клиент · Лист N из M» внизу каждого листа.
// Шаблоны отличаются только внешним видом (КП СПЕЦМАШЭЛЕКТРО/ДИСТИНКТ — общий,
// МОСЭЛ — по своему бланку), тексты и расчёты берут отсюда (решение 14.10.2026).
import type { jsPDF } from 'jspdf'
import { DEFAULT_KP_PHONE } from './kpEntities'

export const KP_NDS_RATE = 22

/** НДС, входящий в сумму с НДС 22%: сумма × 22 / 122. */
export function kpNdsOf(totalWithNds: number): number {
  return (totalWithNds * KP_NDS_RATE) / (100 + KP_NDS_RATE)
}

/** Подписи строк итогов — как в Excel-КП СПЕЦМАШЭЛЕКТРО. */
export const KP_TOTAL_LABEL = 'ВСЕГО с НДС 22%:'
export const KP_NDS_LABEL = 'В том числе НДС 22%:'

/** Заголовок блока условий. */
export const KP_TERMS_TITLE = 'Условия поставки'

/**
 * «Исполнитель / Ответственный / Телефон / Почта» — тот, кто выставляет КП.
 * Телефона в профиле нет — общий номер; почта — только если заполнена.
 */
export function kpResponsibleLines(
  name: string | null | undefined,
  phone: string | null | undefined,
  email: string | null | undefined,
): string[] {
  const who = name?.trim() || ''
  const lines = [
    `Исполнитель: ${who}`,
    `Ответственный: ${who}`,
    `Телефон: ${phone?.trim() || DEFAULT_KP_PHONE}`,
  ]
  if (email?.trim()) lines.push(`Почта: ${email.trim()}`)
  return lines
}

/** Условия поставки для PDF (короткая редакция). */
export const KP_PDF_TERMS = [
  '1. Цены указаны в рублях РФ с учётом НДС 22%.',
  '2. Срок поставки отсчитывается с даты поступления авансового платежа на расчётный счёт поставщика; отгрузка — курьерской службой до склада покупателя за счёт поставщика.',
  '3. Цены и сроки, указанные в таблице, действительны в течение 10 рабочих дней с даты регистрации настоящего письма.',
  '4. В стоимость продукции включены тара, упаковка, транспортные расходы, страхование груза в пути, а также все налоги, пошлины и сборы.',
  '5. Поставщик подтверждает гарантию завода-изготовителя в пределах гарантийного срока, но не более 12 месяцев с момента поставки покупателю (для продукции, снятой с производства, — 3 месяца).',
  '6. Порядок оплаты: авансовый платёж 100%.',
  '7. Комплект документации: счёт, счёт-фактура, товарная накладная (ТОРГ-12/УПД) в двух экземплярах, этикетка.',
] as const

/** Условия поставки для Excel (полная редакция из шаблона Excel). */
export const KP_EXCEL_TERMS = [
  '1. Цены указаны в рублях.',
  '2. Срок поставки указанный в таблице отсчитывается после получения авансового платежа на р/с поставщика, отгрузка осуществляется почтовыми посылками или курьерской службой до склада покупателя за счет средств поставщика.',
  '3. Цены и сроки поставки, указанные в Таблице 1, действительны в течение 10 рабочих дней с даты регистрации письма.',
  '4. В стоимость продукции включены стоимость тары, упаковки, транспортных расходов по доставке, страхования груза в пути следования, а также все налоги, пошлины и сборы.',
  '5. Поставщик подтверждает гарантию завода изготовителя в пределах гарантийного срока, содержащегося в описании Продукции, указанной в документе о качестве на Продукцию (ТУ), либо на сайте изготовителя, но не более 1 (одного) года (12 месяцев) с момента поставки ее Покупателю, за исключением Продукции, снятой с производства заводом-изготовителем. На продукцию, снятую с производства, гарантийный срок составляет 3 (три) месяца, при этом срок проверки Продукции, снятой с производства, составляет 2 (два) месяца от момента отгрузки Продукции Покупателю.',
  '6. Порядок оплаты: аванс 100%',
  '7. Продукция поставляется в комплекте со следующей документацией:\nа: Счет, счет-фактура, выставленные Покупателю;\nб: Товарная накладная по форме ТОРГ-12/УПД в двух экземплярах;\nв: Этикетка.',
] as const

/** Данные для строки листа: по ним опознают потерянный лист распечатки. */
export interface KpPageMark {
  requestNumber?: string | null
  dateStr: string
  entityName: string
  clientName?: string | null
}

const clean = (s: string | null | undefined) => s?.replace(/[\xA0\u202F]/g, ' ').trim() ?? ''

/** «КП № … от … · юрлицо · клиент» — без номера листа. */
export function kpPageMarkText(m: KpPageMark, clientMax = Infinity): string {
  let client = clean(m.clientName)
  if (client.length > clientMax) client = `${client.slice(0, Math.max(1, clientMax - 1)).trim()}…`
  const parts = [`КП № ${clean(m.requestNumber) || '—'} от ${m.dateStr}`, clean(m.entityName)]
  if (client) parts.push(client)
  return parts.filter(Boolean).join(' · ')
}

/**
 * Строка листа для PDF под ширину maxW (мм) текущим шрифтом документа: сначала
 * ужимаем кегль до 6,5 pt, потом укорачиваем имя клиента. Кегль оставляем
 * выставленным — вызывающий сразу печатает строку.
 */
export function fitPdfPageMark(
  doc: jsPDF,
  m: KpPageMark,
  page: number,
  pages: number,
  maxW: number,
  size = 8,
): string {
  const tail = ` · Лист ${page} из ${pages}`
  let s = size
  doc.setFontSize(s)
  const full = kpPageMarkText(m) + tail
  while (s > 6.5 && doc.getTextWidth(full) > maxW) {
    s -= 0.5
    doc.setFontSize(s)
  }
  if (doc.getTextWidth(full) <= maxW) return full
  for (let max = clean(m.clientName).length - 1; max > 4; max -= 2) {
    const t = kpPageMarkText(m, max) + tail
    if (doc.getTextWidth(t) <= maxW) return t
  }
  return kpPageMarkText({ ...m, clientName: null }) + tail
}

/**
 * Нижний колонтитул Excel: та же строка, номер листа Excel ставит сам при
 * печати (&P — текущий, &N — всего). «&» в тексте удваиваем — иначе Excel
 * примет его за код колонтитула.
 */
export function excelPageMarkFooter(m: KpPageMark): string {
  const text = kpPageMarkText(m, 60).replace(/&/g, '&&')
  return `&C&8${text} · Лист &P из &N`
}
